import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

function LicenceSettings({ session, profile, onNavigate }) {
  const [org, setOrg] = useState(null)
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    const { data: orgData } = await supabase
      .from('organisations')
      .select('id, name, licensed_user_count, instance_type, licence_active')
      .eq('id', profile?.organisation_id)
      .single()
    const { data: userData } = await supabase
      .from('profiles')
      .select('id, username, full_name, role, is_active')
      .eq('organisation_id', profile?.organisation_id)
      .eq('is_active', true)
      .order('full_name')
    setOrg(orgData)
    setUsers(userData || [])
    setLoading(false)
  }

  if (loading) return <div className="scorecard-page container"><p style={{ color: 'var(--slate)' }}>Loading...</p></div>

  const totalSeats = org?.licensed_user_count || 5
  const usedSeats = users.length
  const availableSeats = Math.max(0, totalSeats - usedSeats)
  const usagePct = Math.min(100, Math.round((usedSeats / totalSeats) * 100))
  const atCapacity = usedSeats >= totalSeats
  const isSuperAdmin = profile?.role === 'super_admin'

  const ROLE_LABELS = {
    super_admin: 'Super Admin', admin: 'Admin',
    head_of_bids: 'Head of Bid Management', bid_lead: 'Bid Lead',
    contributor: 'Contributor', reviewer: 'Reviewer',
  }

  const INSTANCE_LABELS = {
    shared: 'Shared Multi-Tenant',
    dedicated: 'Dedicated Cloud Instance',
    on_premise: 'On-Premise',
  }

  const handleContact = () => {
    const subject = encodeURIComponent('Cadence Licence - ' + (org?.name || '') + ' - Seat Increase Request')
    const body = encodeURIComponent('Hi,\n\nWe would like to add more seats.\n\nOrganisation: ' + (org?.name || '') + '\nCurrent seats: ' + totalSeats + '\nSeats required: \n\nThank you')
    window.location.href = 'mailto:phillpottsjonathan@hotmail.com?subject=' + subject + '&body=' + body
  }

  return (
    <div className="scorecard-page container">
      <div className="page-header">
        <p className="label">Organisation Settings</p>
        <h1>Licence</h1>
        <p>Current licence status and seat usage for {org?.name}.</p>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card__header">
          <span className="card__title">Seat Usage</span>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: atCapacity ? 'var(--white)' : 'var(--green)', background: atCapacity ? 'var(--red)' : 'var(--green-pale)', padding: '3px 10px' }}>
            {atCapacity ? 'At Capacity' : availableSeats + ' seats available'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '48px', marginBottom: '20px', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '4rem', fontWeight: 700, color: 'var(--green-dark)', lineHeight: 1 }}>{usedSeats}</div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--slate)', marginTop: '4px' }}>Seats Used</div>
          </div>
          <div style={{ fontSize: '2rem', color: 'var(--silver)', paddingBottom: '12px' }}>/</div>
          <div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '4rem', fontWeight: 700, color: 'var(--green-dark)', lineHeight: 1 }}>{isSuperAdmin ? '∞' : totalSeats}</div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--slate)', marginTop: '4px' }}>Licensed Seats</div>
          </div>
        </div>

        {!isSuperAdmin && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ height: '8px', background: 'var(--green-light)', marginBottom: '6px' }}>
              <div style={{ height: '100%', width: usagePct + '%', background: atCapacity ? 'var(--red)' : usagePct >= 80 ? 'var(--amber)' : 'var(--green)', transition: 'width 0.3s' }} />
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--slate)' }}>{usagePct}% of licensed seats in use</div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', paddingTop: '16px', borderTop: '1px solid var(--green-light)' }}>
          <div>
            <div style={{ fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>Instance Type</div>
            <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--charcoal)' }}>{INSTANCE_LABELS[org?.instance_type] || 'Shared Multi-Tenant'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>Licence Status</div>
            <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: org?.licence_active ? 'var(--green)' : 'var(--red)' }}>{org?.licence_active ? 'Active' : 'Inactive'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--green)', marginBottom: '4px' }}>Monthly Cost</div>
            <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--charcoal)' }}>{isSuperAdmin ? 'N/A' : '£' + (totalSeats * 149).toLocaleString() + ' / month'}</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card__header">
          <span className="card__title">Active Users</span>
          <span className="card__weight">{usedSeats} of {isSuperAdmin ? '∞' : totalSeats} seats</span>
        </div>
        {users.map(user => (
          <div key={user.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--green-light)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '32px', height: '32px', background: 'var(--green-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Cormorant Garamond, serif', fontSize: '1rem', fontWeight: 600, color: 'var(--green-dark)' }}>
                {(user.full_name || user.username || '?')[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--charcoal)' }}>
                  {user.full_name || user.username}
                  {user.id === session.user.id && <span style={{ marginLeft: '8px', fontSize: '0.625rem', fontWeight: 600, color: 'var(--green)', background: 'var(--green-pale)', padding: '1px 6px' }}>You</span>}
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--slate)' }}>@{user.username}</div>
              </div>
            </div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--slate)', background: 'var(--off-white)', padding: '3px 10px', border: '1px solid var(--silver)' }}>
              {ROLE_LABELS[user.role] || user.role}
            </div>
          </div>
        ))}
      </div>

      {!isSuperAdmin && (
        <div style={{ background: atCapacity ? '#FFF5F5' : 'var(--green-pale)', borderLeft: '4px solid ' + (atCapacity ? 'var(--red)' : 'var(--green)'), padding: '20px 24px', marginBottom: '24px' }}>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.125rem', fontWeight: 600, color: atCapacity ? 'var(--red)' : 'var(--green-dark)', marginBottom: '8px' }}>
            {atCapacity ? 'You have used all ' + totalSeats + ' licensed seats' : 'Need more seats?'}
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--slate)', margin: '0 0 16px' }}>
            {atCapacity ? 'Contact Captus to increase your capacity before inviting new users.' : 'You have ' + availableSeats + ' seat' + (availableSeats !== 1 ? 's' : '') + ' remaining. Contact Captus to add more when needed.'}
          </p>
          <button onClick={handleContact} style={{ background: atCapacity ? 'var(--red)' : 'var(--green)', color: 'var(--white)', padding: '10px 24px', fontSize: '0.875rem', fontWeight: 600, border: 'none', cursor: 'pointer' }}>
            Contact Captus to Add Seats
          </button>
        </div>
      )}

      <div className="actions">
        <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>← Dashboard</button>
      </div>
    </div>
  )
}

export default LicenceSettings
